import type { SteeringPlan } from '../data/play/types';
/**
 * Overhead world renderer. Plays back VERIFIED step segments (state before → state after) at one step per control
 * window, catches up faster when several steps resolve at once, and holds (jogging in place) when it is waiting for
 * the next resolution. The unresolved next step is drawn only as a labelled projection with an uncertainty wedge.
 * Visual collisions are triggered from canonical encounter data when the displayed runner crosses an object row,
 * using the same straight-segment interpolation as the contracts — so what you see matches what was verified.
 */
import { color } from '@runner/ui';
import { EncounterKind, handlerParams, initialState, movementParams, step as simStep, type RunnerState } from '@runner/simulation';
import type { WorldView, StepView } from '../data/types';
import { drawRunner, drawRunnerSprite, spriteTiming, type RunnerMode } from './runnerArt';
import { preloadRunnerArt, type SpriteState } from './brandArt';
import { drawCrowd, drawGround, drawLabel, drawObject, drawRoad, goldenShoe, objectLabel, preloadSceneArt, roundRect, sparkle, X, Y, type Box, type Cam } from './sceneArt';

interface Particle {
  kind: 'dust' | 'ember' | 'drop' | 'confetti' | 'leaf' | 'spark' | 'steam';
  x: number; // world x
  y: number; // world y
  vx: number;
  vy: number;
  life: number;
  max: number;
  size: number;
  hue: string;
}

interface Footprint {
  x: number;
  y: number;
  age: number;
  side: number;
}

/** Sprite states that share the stride clock (the pack keeps one run-cycle phase across these). */
const RUN_CYCLE: ReadonlySet<SpriteState> = new Set(['run-forward', 'steer-left', 'steer-right', 'overheat']);

const M = movementParams();
const H = handlerParams(18);

export interface RendererOptions {
  reducedMotion: boolean;
  showLabels: boolean;
  /** Landing showcase: no object callouts or routine phase banners; feed warnings still show. */
  ambient?: boolean;
  /** Before launch (launch.json "soon"): no API yet, so the road says so instead of "disconnected". */
  prelaunch?: boolean;
}

export class GameRenderer {
  steeringPreview: SteeringPlan | null = null;
  private lastCamera: Cam | null = null;
  aimAt(screenX: number): number | undefined {
    const c = this.lastCamera;
    return c ? Math.max(-1000, Math.min(1000, Math.round((screenX - c.cx) / c.sx))) : undefined;
  }
  private ctx: CanvasRenderingContext2D;
  private view: WorldView | null = null;
  private runId = -1;
  private display = 0;
  private lastDisplayY = 0;
  private time = 0;
  private particles: Particle[] = [];
  private prints: Footprint[] = [];
  private stride = 0;
  private lastStride = 0;
  private lean = 0;
  private plant = 0;
  private lastVxSign = 0;
  private mode: RunnerMode = 'warmup';
  private modeT = 0;
  private modeUntil = 0;
  private heat = 0;
  private wet = 0;
  private holdUntil = 0;
  /** Current sprite clip and when it started (one-shots play once from here, then hold their last frame). */
  private clip: SpriteState = 'idle';
  private clipStart = 0;
  /** Visual counter-steer clip window after a verified left→right reversal. */
  private counterUntil = 0;
  private triggered = new Set<number>();
  private banner: { text: string; tone: string; until: number } | null = null;
  private dpr = 1;
  private W = 0;
  private Hh = 0;
  catchingUp = false;
  private replaying = false;
  /** Announcements for the screen-reader live region (consumed by the React wrapper). */
  announcements: string[] = [];

  constructor(private canvas: HTMLCanvasElement, public opts: RendererOptions) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas 2d unavailable');
    this.ctx = ctx;
    // Brand art loads in the background; the procedural drawing covers every frame until it has decoded.
    preloadRunnerArt();
    preloadSceneArt();
  }

  resize(cssW: number, cssH: number, dpr: number) {
    this.dpr = Math.min(2, dpr || 1);
    this.W = cssW;
    this.Hh = cssH;
    this.canvas.width = Math.round(cssW * this.dpr);
    this.canvas.height = Math.round(cssH * this.dpr);
  }

  setView(v: WorldView) {
    const id = v.run?.id ?? -1;
    if (id !== this.runId) {
      const joiningMidRun = this.runId === -1 && v.steps.length > 2;
      this.runId = id;
      // Joining a run already in progress: start just before the latest verified step instead of replaying the
      // whole run; a fresh run (or one that starts while watching) plays from the start line.
      this.display = joiningMidRun ? v.steps.length - 1.5 : 0;
      const startY = joiningMidRun ? Number(v.steps[Math.floor(this.display)].before.y) : 0;
      this.lastDisplayY = startY;
      this.triggered.clear();
      if (joiningMidRun && v.run) for (const o of v.run.course) if (Number(o.y) <= startY) this.triggered.add(o.index);
      this.prints = [];
      this.heat = 0;
      this.wet = 0;
      this.holdUntil = 0;
      if (v.run && !joiningMidRun) this.flash(`Run ${v.run.id}: new course`, color.ink, 2.2);
    }
    this.view = v;
  }

  /** Jump playback to the latest verified step (used by "skip to live"). */
  skipToLive() {
    if (this.view) this.display = this.view.steps.length;
  }

  /** Replay the current run's verified steps from the start line (no new data: the same committed segments). */
  replay() {
    if (!this.view || !this.view.steps.length) return;
    this.display = 0;
    this.lastDisplayY = 0;
    this.triggered.clear();
    this.prints = [];
    this.heat = 0;
    this.wet = 0;
    this.replaying = true;
    this.flash(`Replay of run ${this.view.run?.id ?? ''} — verified steps`, color.ink, 2);
  }

  private flash(text: string, tone: string, secs: number) {
    this.banner = { text, tone, until: this.time + secs };
  }

  private setMode(m: RunnerMode, secs: number) {
    this.mode = m;
    this.modeT = 0;
    this.modeUntil = this.time + secs;
  }

  private emit(kind: Particle['kind'], x: number, y: number, n: number, spread = 40) {
    if (this.opts.reducedMotion) return;
    const hues: Record<Particle['kind'], string[]> = {
      dust: [color.sand, color.paper],
      ember: [color.ember, color.orange, color.goldSoft],
      drop: [color.water, color.waterSoft],
      confetti: [color.orange, color.gold, color.harvest, color.cream, color.water],
      leaf: [color.harvest, color.harvestSoft],
      spark: [color.goldSoft, color.white],
      steam: [color.paper],
    };
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = (0.3 + Math.random()) * spread;
      const list = hues[kind];
      this.particles.push({
        kind,
        x,
        y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp + (kind === 'ember' || kind === 'steam' ? 60 : 0),
        life: 0,
        max: kind === 'confetti' ? 1.6 : kind === 'dust' ? 0.7 : 1.1,
        size: kind === 'confetti' ? 4 : kind === 'dust' ? 7 : 3,
        hue: list[Math.floor(Math.random() * list.length)],
      });
    }
    if (this.particles.length > 600) this.particles.splice(0, this.particles.length - 600);
  }

  private verifiedEnd(): RunnerState {
    const s = this.view?.steps;
    return s && s.length ? s[s.length - 1].state : initialState();
  }

  /** Displayed (interpolated) position along the verified trajectory. */
  private displayed(steps: StepView[]): { x: number; y: number; vx: number; seg: StepView | null; pace: number } {
    const n = steps.length;
    if (n === 0) return { x: 0, y: 0, vx: 0, seg: null, pace: 0 };
    if (this.display >= n) {
      const st = steps[n - 1].state;
      return { x: Number(st.x), y: Number(st.y), vx: 0, seg: null, pace: 0 };
    }
    const k = Math.floor(this.display);
    const f = this.display - k;
    const seg = steps[k];
    const x = Number(seg.before.x) + (Number(seg.state.x) - Number(seg.before.x)) * f;
    const y = Number(seg.before.y) + (Number(seg.state.y) - Number(seg.before.y)) * f;
    return { x, y, vx: Number(seg.state.vx), seg, pace: Number(seg.pace) };
  }

  frame(dtRaw: number) {
    const dt = Math.min(0.05, Math.max(0, dtRaw));
    this.time += dt;
    this.modeT += dt;
    const v = this.view;
    const ctx = this.ctx;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    const W = this.W;
    const Hh = this.Hh;
    if (!v || W === 0) {
      ctx.fillStyle = color.paper;
      ctx.fillRect(0, 0, W, Hh);
      return;
    }
    const steps = v.steps;
    const n = steps.length;
    const active = v.run && v.run.status !== 'awaiting-randomness';
    if (this.replaying && this.display >= n) this.replaying = false;

    const reduced = this.opts.reducedMotion;
    // Disconnected or indexer behind: hold at the last verified step, draw no projection, invent nothing.
    const disconnected = v.freshness.api === 'down';
    const indexLag = v.freshness.catchingUp || (v.freshness.pendingSteps ?? 0) > 0;

    // ---- playback clock --------------------------------------------------------------------------
    if (active) {
      const behind = n - this.display;
      let rate = 1 / Math.max(0.5, v.timing.windowSeconds);
      this.catchingUp = !this.replaying && behind > 1.6;
      if (this.catchingUp) rate *= Math.min(6, behind);
      if (this.replaying) rate = 1.25;
      // Reduced motion: no tweening between verified states — jump straight to the latest one.
      this.display = reduced ? n : Math.min(n, this.display + rate * dt);
    }
    const pos = this.displayed(steps);

    // ---- visual triggers from canonical encounters ---------------------------------------------
    if (v.run && pos.y > this.lastDisplayY) {
      for (const o of v.run.course) {
        if (this.triggered.has(o.index)) continue;
        if (Number(o.y) > this.lastDisplayY && Number(o.y) <= pos.y) {
          this.triggered.add(o.index);
          const enc = steps.flatMap((s) => s.encounters).find((e) => e.objectIndex === o.index);
          this.onCross(o.kind, enc?.hit ?? false, Number(o.xL), Number(o.xR), Number(o.y), pos.x, enc?.amount ?? 0n);
        }
      }
    }
    this.lastDisplayY = pos.y;

    // ---- runner mode -----------------------------------------------------------------------------
    if (this.time > this.modeUntil) {
      const waiting = active && this.display >= n && v.run?.status === 'active';
      let next: RunnerMode = 'run';
      if (disconnected) next = 'rest';
      else if (!v.run || v.run.status === 'awaiting-randomness') next = 'warmup';
      else if (v.phase === 'preview') next = 'warmup';
      else if (v.run.status === 'finished' && this.display >= n) next = 'rest';
      else if (this.replaying) next = 'replay';
      else if (waiting) next = 'wait';
      else if (this.catchingUp) next = 'replay';
      if (next !== this.mode) {
        this.mode = next;
        this.modeT = 0;
      }
    }
    const moving = this.mode === 'run' || this.mode === 'replay' || this.mode === 'collect' || this.mode === 'starting';
    const leanTarget = moving ? Math.max(-1, Math.min(1, pos.vx / Number(M.vxMax))) : 0;
    this.lean = reduced ? leanTarget : this.lean + (leanTarget - this.lean) * Math.min(1, dt * 6);
    const vxSign = Math.sign(pos.vx);
    if (moving && vxSign !== 0 && this.lastVxSign !== 0 && vxSign !== this.lastVxSign) {
      this.plant = reduced ? 0 : 1;
      // The pack's counter-steer clip runs left→right only; reversing it would also reverse foot timing, so a
      // right→left reversal blends straight into steer-left instead.
      if (vxSign > 0 && !reduced) this.counterUntil = this.time + 0.8;
      this.emit('dust', pos.x, pos.y, 10, 60);
      this.announce(vxSign > 0 ? 'RUNNER counter-steers right.' : 'RUNNER counter-steers left.');
    }
    if (vxSign !== 0) this.lastVxSign = vxSign;
    this.plant = Math.max(0, this.plant - dt * 2.5);
    this.heat = Math.max(0, this.heat - dt * 0.12);
    this.wet = Math.max(0, this.wet - dt * 0.35);

    const cadence = reduced ? 0 : moving ? 1.55 * (0.6 + (pos.pace || Number(M.basePace)) / Number(M.basePace) * 0.5) * (this.catchingUp ? 1.6 : 1) : this.mode === 'wait' || this.mode === 'warmup' ? 1.1 : 0;
    this.lastStride = this.stride;
    this.stride = (this.stride + dt * cadence) % 1;
    if (moving && ((this.lastStride < 0.5 && this.stride >= 0.5) || this.stride < this.lastStride)) {
      const side = this.stride >= 0.5 ? 1 : -1;
      this.prints.push({ x: pos.x + side * 18, y: pos.y, age: 0, side });
      if (this.heat > 0.3) this.emit('ember', pos.x, pos.y, 2, 20);
      else if (Math.random() < 0.35) this.emit('dust', pos.x + side * 16, pos.y - 5, 1, 12);
    }
    for (const p of this.prints) p.age += dt;
    this.prints = this.prints.filter((p) => p.age < 3.5);

    // ---- camera ----------------------------------------------------------------------------------
    const narrow = W < 640;
    const roadHalf = narrow ? W * 0.4 : Math.min(W * 0.34, 360);
    const sx = roadHalf / 1000;
    const forwardRange = narrow ? 1250 : 1450;
    const sy = Hh / forwardRange;
    const cam: Cam = {
      W,
      H: Hh,
      cx: W / 2,
      sx,
      sy,
      camY: pos.y,
      anchorY: Hh * 0.74,
      sprite: Math.max(0.62, Math.min(1.25, Math.min(sx * 3.1, sy * 2.6))),
    };

    this.lastCamera = cam;

    // ---- draw ------------------------------------------------------------------------------------
    // Reduced motion: every decorative clock is frozen, so the frame only changes when verified state changes.
    const tDraw = reduced ? 0 : this.time;
    drawGround(ctx, cam, tDraw, reduced);
    drawRoad(ctx, cam, v.run?.course ?? [], !!this.opts.showLabels);
    for (const p of this.prints) {
      ctx.globalAlpha = Math.max(0, 0.22 * (1 - p.age / 3.5));
      ctx.fillStyle = this.heat > 0.3 ? color.ember : color.ink;
      ctx.beginPath();
      ctx.ellipse(X(cam, p.x), Y(cam, p.y), 3 * cam.sprite, 5 * cam.sprite, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    const course = v.run?.course ?? [];
    const resolved = new Map<number, boolean>();
    for (const s of steps) for (const e of s.encounters) resolved.set(e.objectIndex, e.hit);
    for (const o of course) {
      const showResolved = this.triggered.has(o.index);
      drawObject(ctx, cam, o, tDraw, reduced, { resolved: showResolved, hit: resolved.get(o.index) });
    }

    // Projection of the unresolved next step from the last VERIFIED state.
    if (v.run && v.run.status === 'active' && v.openInput && v.phase === 'running' && v.stage === 'input-open' && !this.replaying && !disconnected && !indexLag && !v.freshness.stale && !this.catchingUp) this.drawProjection(cam, v);

    drawCrowd(ctx, cam, tDraw, reduced, pos.y);

    // Runner
    const preview = this.steeringPreview;
    if (preview && !disconnected && !indexLag && preview.runId === v.run?.id && preview.window === v.openInput?.window && Date.now() - preview.createdAt < 10000) {
      ctx.save();
      const x0 = X(cam, Number(preview.from.x)), y0 = Y(cam, Number(preview.from.y));
      const x1 = X(cam, Number(preview.predicted.x)), y1 = Y(cam, Number(preview.predicted.y));
      ctx.strokeStyle = '#235E96'; ctx.lineWidth = 4; ctx.setLineDash([8, 5]);
      ctx.beginPath(); ctx.moveTo(x0, y0); for (const point of preview.path) ctx.lineTo(X(cam, Number(point.x)), Y(cam, Number(point.y))); ctx.stroke(); ctx.setLineDash([]);
      ctx.fillStyle = '#FFFCF6'; ctx.beginPath(); ctx.arc(x1, y1, 12, 0, Math.PI*2); ctx.fill(); ctx.stroke();
      ctx.font = '600 12px system-ui'; ctx.textAlign = 'center';
      const label = 'Estimated next step'; const width = ctx.measureText(label).width + 16;
      ctx.fillStyle = '#FFFCF6'; ctx.fillRect(x1-width/2, y1-38, width, 22);
      ctx.fillStyle = '#235E96'; ctx.fillText(label, x1, y1-23);
      const tx = X(cam, preview.targetX);
      ctx.strokeStyle = '#235E96'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(tx-7,y1-7); ctx.lineTo(tx+7,y1+7); ctx.moveTo(tx+7,y1-7); ctx.lineTo(tx-7,y1+7); ctx.stroke();
      ctx.restore();
    }

    const rx = X(cam, pos.x);
    const ry = Y(cam, pos.y);
    // Keep the playable character readable on phones; its verified position and collision geometry do not change.
    const rs = cam.sprite * (narrow ? 1.2 : 1.05);
    const finished = v.run?.status === 'finished' && this.display >= n;
    const want = this.spriteState(finished);
    if (want !== this.clip) {
      this.clip = want;
      this.clipStart = this.time;
    }
    ctx.save();
    ctx.translate(rx, ry);
    // 256-unit sprite frame at ~118 CSS px per sprite unit, capped at 128 px so a DPR-2 screen never upscales the 1× atlas.
    const drawn = this.drawSprite(Math.min(128, 118 * rs), reduced, moving);
    if (!drawn) {
      if (this.heat > 0.05 && !reduced) {
        const g = ctx.createRadialGradient(0, -30 * cam.sprite, 4, 0, -30 * cam.sprite, 46 * cam.sprite);
        g.addColorStop(0, `rgba(236,103,58,${0.45 * this.heat})`);
        g.addColorStop(1, 'rgba(236,103,58,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(0, -30 * cam.sprite, 46 * cam.sprite, 0, Math.PI * 2);
        ctx.fill();
      }
      drawRunner(
        ctx,
        {
          t: tDraw,
          phase: reduced ? 0.25 : this.stride,
          speed: (pos.pace || Number(M.basePace)) / Number(M.basePace),
          lean: this.lean,
          plant: this.plant,
          mode: this.mode,
          modeT: reduced ? 10 : this.modeT,
          heat: this.heat,
          wet: this.wet,
          holdingShoe: this.time < this.holdUntil,
        },
        rs,
      );
    }
    ctx.restore();

    // Particles (world space)
    this.stepParticles(dt, cam);

    // Labels for upcoming objects
    if (this.opts.showLabels && !this.opts.ambient && v.run) {
      let side: 1 | -1 = 1;
      // Keep-out box around the runner sprite (feet at the anchor, body above it).
      // …and the DOM overlay controls (pause / labels / reduced motion) in the top-right corner.
      const avoid: Box[] = [
        { x0: rx - 34 * rs, y0: ry - 92 * rs, x1: rx + 34 * rs, y1: ry + 10 * rs, clear: 'up' },
        { x0: W - 64, y0: 40, x1: W, y1: 220, clear: 'left' },
      ];
      for (const o of course) {
        // Objects already behind the runner lose their label; the outcome banner reports the crossing.
        if (Number(o.y) < pos.y - 4) continue;
        const shoe = v.run.shoe && v.run.shoe.objectIndex === o.index ? v.run.shoe : null;
        const lab = objectLabel(o, {
          quoteSymbol: v.quote.symbol,
          shoeAmount: shoe ? fmtAmount(shoe.amount, v.quote.decimals) : undefined,
          shoeStatus: shoe ? shoeStatusText(shoe.status, v.timing.now, shoe.cutoff) : undefined,
        });
        if (!lab) continue;
        const r = resolved.get(o.index);
        const shown = this.triggered.has(o.index) && r !== undefined ? { ...lab, detail: resolvedText(o.kind, r) } : lab;
        drawLabel(ctx, cam, o, shown, side, avoid);
        side = side === 1 ? -1 : 1;
      }
    }

    this.drawBanner(cam, v);
  }

  /**
   * Pack sprite state for the current (verified-data-driven) RunnerMode, lean and heat. Purely presentational:
   * modes come from canonical encounters and the playback clock, never from the animation.
   */
  private spriteState(finished: boolean): SpriteState {
    switch (this.mode) {
      case 'warmup':
        return 'idle';
      case 'starting':
        return 'start';
      case 'stumble':
        return 'stumble';
      case 'cool':
        return 'cool-recover';
      case 'collect':
      case 'harvest':
        return 'shoe-pickup';
      case 'finish':
        return 'finish';
      case 'wait':
        return 'waiting';
      case 'rest':
        // After a finish the clip holds its final rest pose; a feed outage rests in place (idle) instead.
        return finished ? 'finish' : 'idle';
      case 'overheat':
        return 'overheat';
      case 'forge':
      case 'run':
      case 'replay':
      default:
        if (this.time < this.counterUntil) return 'counter-steer';
        if (this.heat > 0.3) return 'overheat';
        if (this.lean > 0.35) return 'steer-right';
        if (this.lean < -0.35) return 'steer-left';
        return 'run-forward';
    }
  }

  /** Draw the current clip's frame at the origin; falls back to a loaded neighbouring state, else returns false. */
  private drawSprite(size: number, reduced: boolean, moving: boolean): boolean {
    const candidates: SpriteState[] = [this.clip, moving ? 'run-forward' : 'idle'];
    for (const state of candidates) {
      const tm = spriteTiming(state);
      if (!tm) continue;
      let frame: number;
      if (reduced) {
        // Reduced motion: hold one representative frame (loops: first; one-shots: their final pose).
        frame = tm.loop ? 0 : tm.frames - 1;
      } else if (RUN_CYCLE.has(state)) {
        frame = Math.floor(this.stride * tm.frames) % tm.frames;
      } else {
        const p = ((this.time - this.clipStart) * 1000) / tm.durationMs;
        frame = tm.loop ? Math.floor((p % 1) * tm.frames) : Math.min(tm.frames - 1, Math.floor(p * tm.frames));
      }
      // Overlays are cosmetic and off in reduced motion; the Harvest celebration borrows the pickup pose, not its ribbons.
      const effect = !reduced && state === this.clip && this.mode !== 'harvest';
      if (drawRunnerSprite(this.ctx, state, frame, size, { effect, shadow: true })) return true;
    }
    return false;
  }

  private drawProjection(cam: Cam, v: WorldView) {
    const ctx = this.ctx;
    const end = this.verifiedEnd();
    const course = (v.run?.course ?? []).map(({ kind, y, xL, xR, param }) => ({ kind, y, xL, xR, param }));
    const cursor = (v.run?.course ?? []).findIndex((o) => o.y > end.y);
    const cur = cursor < 0 ? course.length : cursor;
    const net = v.openInput!.net;
    const vol = v.openInput!.volume;
    let proj: RunnerState;
    let lo: RunnerState;
    let hi: RunnerState;
    try {
      proj = simStep(end, course, cur, net, vol, v.refSupply, M, H, v.run?.engineVersion ?? 1).state;
      const big = v.refSupply; // saturating input in each direction bounds the possible outcome of this step
      lo = simStep(end, course, cur, -big, vol, v.refSupply, M, H, v.run?.engineVersion ?? 1).state;
      hi = simStep(end, course, cur, big, vol, v.refSupply, M, H, v.run?.engineVersion ?? 1).state;
    } catch {
      return;
    }
    const x0 = X(cam, Number(end.x));
    const y0 = Y(cam, Number(end.y));
    // uncertainty wedge: every outcome still reachable this window
    ctx.fillStyle = 'rgba(11,11,11,0.10)';
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(X(cam, Number(lo.x)), Y(cam, Number(lo.y)));
    ctx.lineTo(X(cam, Number(hi.x)), Y(cam, Number(hi.y)));
    ctx.closePath();
    ctx.fill();
    // verified endpoint marker
    ctx.strokeStyle = color.ink;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x0, y0, 6, 0, Math.PI * 2);
    ctx.stroke();
    // projected tendency
    ctx.setLineDash([6, 6]);
    ctx.strokeStyle = net >= 0n ? color.harvest : color.forge;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(X(cam, Number(proj.x)), Y(cam, Number(proj.y)));
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = 'rgba(255,242,216,0.92)';
    const px = X(cam, Number(proj.x));
    const py = Y(cam, Number(proj.y));
    ctx.beginPath();
    ctx.arc(px, py, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = net >= 0n ? color.harvest : color.forge;
    ctx.stroke();
  }

  private onCross(kind: number, hit: boolean, xL: number, xR: number, y: number, x: number, amount: bigint) {
    const cx = (xL + xR) / 2;
    switch (kind) {
      case EncounterKind.BARRIER:
        if (hit) {
          this.setMode('stumble', 1.0);
          this.emit('dust', x, y, 22, 90);
          this.flash('Barrier hit — stumble, slowed', color.orange, 1.8);
          this.announce('RUNNER hit a barrier and stumbled.');
        } else this.announce('RUNNER cleared a barrier.');
        break;
      case EncounterKind.BURNING_BARRICADE:
        if (hit) {
          this.heat = 1;
          this.setMode('run', 0.2);
          this.emit('ember', x, y, 30, 70);
          this.flash('Overheat! Slices of this run’s budget go to Forge (capped)', color.forge, 2.4);
          this.announce('RUNNER hit the burning barricade: Overheat.');
        } else this.announce('RUNNER avoided the burning barricade.');
        break;
      case EncounterKind.WATER_STATION:
        if (hit) {
          this.heat = 0;
          this.wet = 1;
          this.setMode('cool', 0.9);
          this.emit('drop', x, y, 26, 80);
          this.flash('Water: cooled down · a slice of the run budget to holders', color.water, 2.2);
          this.announce('RUNNER took water: effects cleared, a slice of the run budget assigned to the holder cohort, deferred rewards released.');
        }
        break;
      case EncounterKind.GOLDEN_SHOE:
        if (hit) {
          this.setMode('collect', 1.0);
          this.holdUntil = this.time + 3.2;
          this.emit('spark', cx, y, 26, 90);
          this.emit('confetti', cx, y, 30, 120);
          this.flash('Golden Shoe collected! Winner is drawn from sealed entries', color.gold, 2.6);
          this.announce('RUNNER collected the Golden Shoe.');
        } else {
          this.flash('Golden Shoe missed — unawarded prize returns to the reserve', color.muted, 2.4);
          this.announce('RUNNER missed the Golden Shoe.');
        }
        break;
      case EncounterKind.FORGE_GATE:
        if (hit) {
          this.setMode('forge', 0.9);
          this.emit('ember', x, y, 24, 80);
          this.flash(amount > 0n ? 'Forge gate: buyback authorization activated' : 'Forge gate passed — reserve empty, nothing authorized', color.forge, 2.4);
          this.announce('RUNNER passed the Forge gate.');
        }
        break;
      case EncounterKind.FORK:
        this.emit(x >= 0 ? 'leaf' : 'ember', x, y, 18, 70);
        this.flash(x >= 0 ? 'Right fork → Harvest exit for this run’s budget' : 'Left fork → Forge exit for this run’s budget', x >= 0 ? color.harvest : color.forge, 2.4);
        this.announce(x >= 0 ? 'RUNNER took the Harvest fork.' : 'RUNNER took the Forge fork.');
        break;
      case EncounterKind.PUDDLE:
        if (hit) {
          this.emit('drop', x, y, 14, 50);
          this.flash('Puddle — slowed for one step', color.water, 1.6);
          this.announce('RUNNER splashed through a puddle.');
        }
        break;
      case EncounterKind.FINISH:
        this.setMode('finish', 2.2);
        this.emit('confetti', x, y, 60, 150);
        this.flash('Finish! The rest of the run budget follows the exit route', color.ink, 3);
        this.announce('RUNNER finished the run.');
        break;
      default:
        break;
    }
  }

  private announce(s: string) {
    this.announcements.push(s);
    if (this.announcements.length > 20) this.announcements.shift();
  }

  private stepParticles(dt: number, cam: Cam) {
    const ctx = this.ctx;
    for (const p of this.particles) {
      p.life += dt;
      p.x += (p.vx * dt) / 1;
      p.y += (p.vy * dt) / 1;
      p.vx *= 0.96;
      if (p.kind === 'confetti' || p.kind === 'drop') p.vy -= 120 * dt;
      const a = 1 - p.life / p.max;
      if (a <= 0) continue;
      ctx.globalAlpha = Math.max(0, a);
      const sx = X(cam, p.x);
      const sy = Y(cam, p.y);
      ctx.fillStyle = p.hue;
      if (p.kind === 'confetti') {
        ctx.save();
        ctx.translate(sx, sy);
        ctx.rotate(p.life * 8);
        ctx.fillRect(-p.size, -p.size / 2, p.size * 2, p.size);
        ctx.restore();
      } else if (p.kind === 'spark') sparkle(ctx, sx, sy, p.size * 0.8);
      else if (p.kind === 'dust' || p.kind === 'steam') {
        ctx.beginPath();
        ctx.arc(sx, sy, p.size * (0.6 + p.life * 2), 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.arc(sx, sy, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
    this.particles = this.particles.filter((p) => p.life < p.max);
  }

  private drawBanner(cam: Cam, v: WorldView) {
    const ctx = this.ctx;
    let text: string | null = null;
    let tone: string = color.ink;
    // Before launch the launch bar says so; no road label (it would sit under the title chip).
    if (this.opts.prelaunch && v.freshness.api !== 'ok') return;
    if (v.freshness.api === 'down') {
      text = 'Reconnecting…';
      tone = color.gold;
    } else if (v.freshness.catchingUp) {
      text = 'Syncing…';
      tone = color.gold;
    } else if (this.opts.ambient) {
      return;
    } else if (this.banner && this.time < this.banner.until) {
      text = this.banner.text;
      tone = this.banner.tone;
    } else if (v.phase === 'funding') {
      text = 'Run funded — waiting for randomness to reveal the course';
    } else if (v.phase === 'preview') {
      text = 'Course revealed — steering opens soon';
    } else if (this.catchingUp) {
      text = 'Catching up on resolved steps';
      tone = color.water;
    } else if (v.phase === 'warm-up') {
      text = v.run?.status === 'finished' ? 'Warm-up — the next run is not funded yet (replay available)' : 'Warm-up — the next run is not funded yet';
    } else if (v.phase === 'replay' && !this.replaying) {
      text = 'Between runs — replay available';
    }
    if (!text) return;
    const fs = cam.W < 640 ? 12 : 14;
    ctx.font = `800 ${fs}px system-ui, sans-serif`;
    const w = Math.min(cam.W - 24, ctx.measureText(text).width + 24);
    const x = (cam.W - w) / 2;
    const y = 12;
    ctx.fillStyle = 'rgba(255,242,216,0.96)';
    ctx.strokeStyle = color.ink;
    ctx.lineWidth = 2;
    roundRect(ctx, x, y, w, fs + 16, 10);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = tone;
    ctx.fillRect(x + 8, y + 7, 5, fs + 2);
    ctx.fillStyle = color.ink;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, cam.W / 2 + 4, y + (fs + 16) / 2, w - 24);
  }
}

function fmtAmount(v: bigint, decimals: number): string {
  const base = 10n ** BigInt(decimals);
  const whole = v / base;
  const frac = ((v % base) * 100n) / base;
  return `${whole}.${frac.toString().padStart(2, '0')}`;
}

function shoeStatusText(status: string, now: number, cutoff: number): string {
  if (status === 'Open') return now < cutoff ? `entries close in ${Math.max(0, Math.ceil(cutoff - now))}s` : 'entries sealed';
  if (status === 'Sealed') return 'entries sealed · reach it to award';
  return status.toLowerCase();
}

function resolvedText(kind: number, hit: boolean): string {
  switch (kind) {
    case EncounterKind.BARRIER:
      return hit ? 'HIT — stumbled' : 'cleared';
    case EncounterKind.BURNING_BARRICADE:
      return hit ? 'HIT — Overheat' : 'avoided';
    case EncounterKind.WATER_STATION:
      return hit ? 'taken — effects cleared' : 'missed';
    case EncounterKind.GOLDEN_SHOE:
      return hit ? 'COLLECTED — draw from sealed entries' : 'missed — prize returns to reserve';
    case EncounterKind.FORGE_GATE:
      return hit ? 'passed — authorization' : 'not passed';
    case EncounterKind.FINISH:
      return 'finished';
    default:
      return hit ? 'hit' : 'missed';
  }
}

export { goldenShoe };
